import {
  HttpDownloadProgressEvent,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { concatMap, filter, map, scan } from 'rxjs/operators';
import { ViewResponse } from '../models/agent-chat.models';

export type AgentStreamEvent =
  | { kind: 'delta'; text: string }
  | { kind: 'reset' }
  | { kind: 'reply_done' }
  | { kind: 'reply_split'; leadIn: string; ask: string }
  | { kind: 'progress'; text: string; stage: string | null; holds: string[] }
  | { kind: 'done'; response: ViewResponse }
  | { kind: 'other' };

interface SseFrame {
  event: string;
  data: string;
}

/** An in-band `error` frame: the turn failed, as opposed to the transport dropping. */
export class AgentTurnErrorFrame extends HttpErrorResponse {}

const FRAME_SEPARATOR = '\n\n';

const parseFrame = (raw: string): SseFrame | null => {
  let event = '';
  const data: string[] = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      data.push(line.slice('data:'.length).trim());
    }
  }
  return event ? { event, data: data.join('\n') } : null;
};

const toStreamEvent = (frame: SseFrame): AgentStreamEvent => {
  const payload = frame.data ? JSON.parse(frame.data) : {};
  switch (frame.event) {
    case 'delta':
      return { kind: 'delta', text: payload.text ?? '' };
    case 'reset':
      return { kind: 'reset' };
    case 'reply_done':
      return { kind: 'reply_done' };
    case 'reply_split':
      return { kind: 'reply_split', leadIn: payload.lead_in ?? '', ask: payload.ask ?? '' };
    case 'progress':
      return {
        kind: 'progress',
        text: payload.text ?? '',
        stage: payload.stage ?? null,
        holds: payload.holds ?? [],
      };
    case 'done':
      return { kind: 'done', response: payload as ViewResponse };
    case 'error':
      throw new AgentTurnErrorFrame({
        status: payload.status ?? 500,
        statusText: payload.message ?? 'agent turn failed',
        error: payload,
      });
    default:
      return { kind: 'other' };
  }
};

export const sseEvents = (source$: Observable<HttpEvent<string>>): Observable<AgentStreamEvent> =>
  source$.pipe(
    filter(
      (event): event is HttpDownloadProgressEvent => event.type === HttpEventType.DownloadProgress,
    ),
    scan(
      (state: { consumed: number; frames: SseFrame[] }, event) => {
        const body = event.partialText ?? '';
        const end = body.lastIndexOf(FRAME_SEPARATOR);
        if (end < 0 || end + FRAME_SEPARATOR.length <= state.consumed) {
          return { consumed: state.consumed, frames: [] };
        }
        const consumed = end + FRAME_SEPARATOR.length;
        const frames = body
          .slice(state.consumed, consumed)
          .split(FRAME_SEPARATOR)
          .map(parseFrame)
          .filter((frame): frame is SseFrame => frame !== null);
        return { consumed, frames };
      },
      { consumed: 0, frames: [] as SseFrame[] },
    ),
    concatMap((state) => state.frames),
    map(toStreamEvent),
    filter((event) => event.kind !== 'other'),
  );
